import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IPortinSlot } from "../IPortinSlot";
import { useWatch } from "react-hook-form";
import type { FieldError, FieldName, FieldValues, UseFormMethods } from "react-hook-form";
import { format, isWithinInterval, parse } from "date-fns";
import { TimeTextField } from "@common/TimeTextField/TimeTextField";
import { getObjectByPath } from "@utils/getObjectByPath";

interface IParams<TFieldValues extends FieldValues = FieldValues> {
    portinSlotList: IPortinSlot[];
    formMethods: UseFormMethods<TFieldValues>;
    namePrefix?: string;
    defaultValue?: string;
    clearCardError?(): void;
}

export const PortInTimeRange = <TFieldValues extends FieldValues = FieldValues>(props: IParams<TFieldValues>) => {
    const { formMethods, portinSlotList, namePrefix, defaultValue, clearCardError } = props;
    const { register, clearErrors, errors } = formMethods;
    const { t } = useTranslation(["portin", "validation"]);
    const [portingSlotForDate, setPortingSlotForDate] = useState<IPortinSlot>();
    const [hasNoTimeForSelectedDate, setHasNoTimeForSelectedDate] = useState(false);
    const dateFieldName = namePrefix ? `${namePrefix}date` : "date";
    const timeFieldName = namePrefix ? `${namePrefix}time` : "time";
    const timeFieldError = getObjectByPath<FieldError>(errors, timeFieldName);

    const formatTime = (time: Date) => format(time, "HH:mm");
    const date = useWatch<Date>({ name: dateFieldName, control: formMethods.control });

    const checkIsInTimeRange = (newValue: string) => {
        if (portinSlotList.length) {
            if (portingSlotForDate && date) {
                const { endTime, startTime } = portingSlotForDate;
                const selectedTime = parse(newValue, "HH:mm", date);

                if (!isWithinInterval(selectedTime, { start: startTime, end: endTime })) {
                    const startTimeFormatted = formatTime(startTime);
                    const endTimeFormatted = formatTime(endTime);

                    return `Must be after ${startTimeFormatted} and before ${endTimeFormatted}`;
                }
            } else {
                return "No time available for this move date";
            }
        }

        return undefined;
    };

    useEffect(() => {
        const formattedDate = date ? format(date, "yyyy-MM-dd") : "";
        const portingSlotList = portinSlotList.find(
            (portinSlot) => format(portinSlot.date, "yyyy-MM-dd") === formattedDate
        );
        setPortingSlotForDate(portingSlotList);

        const hasTimeRange = !date || Boolean(portingSlotList);
        setHasNoTimeForSelectedDate(Boolean(date) && !hasTimeRange);

        clearErrors(timeFieldName as FieldName<TFieldValues>);
    }, [clearErrors, date, portinSlotList, timeFieldName]);

    return (
        <TimeTextField
            InputLabelProps={{
                shrink: true,
            }}
            defaultValue={defaultValue}
            hasError={Boolean(timeFieldError)}
            helperText={hasNoTimeForSelectedDate ? t("portin:noTimeAvailableForMoveDate") : timeFieldError?.message}
            iconAriaLabel={t("portin:openTimePicker")}
            id="time"
            inputProps={{
                step: 300, // All 5 minute with arrow
            }}
            isDisabled={hasNoTimeForSelectedDate}
            isRequired
            labelText={t("portin:time")}
            name={timeFieldName}
            register={register}
            registerOptions={{
                required: t("validation:required"),
                validate: (value: string) => checkIsInTimeRange(value),
            }}
            type="time"
            onChange={() => {
                clearErrors(timeFieldName as FieldName<TFieldValues>);
                clearCardError?.();
            }}
        />
    );
};
